import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery, Drawer, IconButton, Badge } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import Customization from '../Customization';
import navigation from 'menu-items';
import { drawerWidth } from 'store/constant';
import { SET_MENU } from 'store/actions';

// assets
import { IconChevronRight } from '@tabler/icons';
import { IconMessage } from '@tabler/icons';
import Chat from 'views/chat';
import { useGetConversationsQuery } from 'services/chat/chatAPIs';
import { useAppUserActiveClientsListingQuery } from 'services/user-management/app-users/appUsersAPIs';
import { useGetUserProfileQuery } from 'services/userApi';
import { useConnectToFirebaseMutation } from 'services/firebase';
import { requestFirebaseToken, subscribeToTopic, listenForMessages } from 'frebase/fmcToken&listner';
import { useNavigate } from 'react-router-dom';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  ...theme.typography.mainContent,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  transition: theme.transitions.create(
    'margin',
    open
      ? {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen
        }
      : {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen
        }
  ),
  [theme.breakpoints.up('md')]: {
    marginLeft: open ? 0 : -(drawerWidth - 20),
    width: `calc(100% - ${drawerWidth}px)`
  },
  [theme.breakpoints.down('md')]: {
    marginLeft: '20px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: '10px',
    width: `calc(100% - ${drawerWidth}px)`,
    padding: '16px',
    marginRight: '10px'
  }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const theme = useTheme();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // Handle left drawer
  const leftDrawerOpened = useSelector((state) => state.customization.opened);
  const dispatch = useDispatch();
  const handleLeftDrawerToggle = () => {
    dispatch({ type: SET_MENU, opened: !leftDrawerOpened });
  };

  const [isChatOpen, setIsChatOpen] = useState(false);

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
  };

  // Fetch user profile and subscribe to FCM topic on mount (when permission already granted)
  const { data: userData } = useGetUserProfileQuery();
  const [connectToFirebase] = useConnectToFirebaseMutation();
  const hasSubscribedRef = useRef(false);
  const hasListenerSetRef = useRef(false);

  useEffect(() => {
    if (!userData?.userName || hasSubscribedRef.current) return;
    const subscribe = async () => {
      const token = await requestFirebaseToken();
      if (token) {
        try {
          await subscribeToTopic(connectToFirebase, token, userData.userName);
          hasSubscribedRef.current = true;
        } catch (err) {
          console.error('FCM subscribe failed:', err);
        }
      }
    };
    subscribe();
  }, [userData?.userName, connectToFirebase]);

  // Set up Firebase message listener for foreground notifications
  useEffect(() => {
    if (hasListenerSetRef.current) return;
    listenForMessages(dispatch, navigate);
    hasListenerSetRef.current = true;
  }, [dispatch, navigate]);

  // Fetch conversations to compute unread count
  const { data: conversationsData } = useGetConversationsQuery({ from: 1, limit: 1500 });
  // Only count unread for ACTIVE-client chats (expired clients' old unread isn't
  // shown in the Active list, so it looked like an unclearable phantom count).
  const { data: allActiveClientsData } = useAppUserActiveClientsListingQuery({ limit: 2000, from: 0, searchName: '' });
  const totalUnreadCount = useMemo(() => {
    const rooms = conversationsData?.chatRooms || [];
    const activeIds = new Set((allActiveClientsData?.users || []).map((u) => String(u._id)));
    return rooms.reduce((sum, room) => {
      const parts = room?.chatRoom?.participants || [];
      const client = parts.find((p) => Number(p?.userType) === 3) || parts[0];
      const clientId = client?._id ? String(client._id) : '';
      return activeIds.has(clientId) ? sum + (room?.unreadCount || 0) : sum;
    }, 0);
  }, [conversationsData, allActiveClientsData]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {/* header */}
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        // The Berry template animated `width` here, but this AppBar is position:fixed and
        // nothing ever changes its width — it was animating a property that never moves,
        // at the cost of a layout-thrashing transition on every sidebar toggle. The bar
        // that actually slides is `Main`, which transitions margin below.
        sx={{ bgcolor: 'surface.chrome' }}
      >
        <Toolbar sx={{ background: 'surface.chrome', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>

      {/* drawer */}
      <Sidebar drawerOpen={!matchDownMd ? leftDrawerOpened : !leftDrawerOpened} drawerToggle={handleLeftDrawerToggle} />

      {/* main content */}
      <Main theme={theme} open={leftDrawerOpened}>
        {/* breadcrumb */}
        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
        <Outlet />
      </Main>
      {/* <Customization /> */}

      {/* Floating Chat Button */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1300
        }}
      >
        <Badge
          color="error"
          badgeContent={totalUnreadCount > 0 ? (totalUnreadCount > 99 ? '99+' : totalUnreadCount) : 0}
          invisible={totalUnreadCount === 0}
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{
            '& .MuiBadge-badge': {
              right: 2,
              top: 2,
              fontSize: '0.6875rem',
              fontWeight: 700,
              // A ring in the page ground separates the count from the button beneath it,
              // so a red badge on a purple circle stays readable.
              border: '2px solid',
              borderColor: 'background.default'
            }
          }}
        >
          <IconButton
            color="primary"
            onClick={toggleChat}
            aria-label={totalUnreadCount > 0 ? `Open chat, ${totalUnreadCount} unread` : 'Open chat'}
            sx={{
              backgroundColor: 'primary.main',
              color: '#fff',
              width: 48,
              height: 48,
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
              '&:hover': {
                backgroundColor: 'secondary.dark'
              }
            }}
          >
            <IconMessage />
          </IconButton>
        </Badge>
      </Box>

      {/* Chat Drawer */}
      <Drawer
        anchor="right"
        open={isChatOpen}
        onClose={toggleChat}
        sx={{
          '& .MuiDrawer-paper': {
            width: '1340px',
            maxWidth: '90%',
            overflowY: 'auto', // Add vertical scrolling
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto', // Ensure internal scrolling
            padding: 2 // Add some padding
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto', // Scrollable content area
              position: 'relative'
            }}
          >
            <Chat />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default MainLayout;
